import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Monarchy: Leadership & Governance
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      The Monarch is the heart of the kingdom—the figurehead who represents its
      people to the outside world. All official communications, negotiations,
      and kingdom-wide decisions pass through the Monarch. Because of this, a
      strong ruler must possess diplomatic skill, strategic foresight, and the
      ability to unite their people.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      It falls upon you and your kingdom to elect a Monarch, or perhaps, if you
      are ambitious enough, to rise to the throne yourself.
    </p>
  </div>
);

const BecomingMonarchSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Becoming Monarch</h3>
    <p className="text-[#b8b8b8] mb-2">
      Monarchy is not inherited—it is earned. To become Monarch, you must be
      elected by your fellow provinces. The process is simple:
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>The candidate with at least half of the kingdom's weighted votes becomes Monarch.</li>
      <li>However, earning those votes is the real challenge.</li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      A successful candidate must prove their strength in war, their honor in
      leadership, and their wisdom in diplomacy. Gaining the trust and support
      of your fellow rulers requires communication, reliability, and a clear
      vision for the kingdom.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Each province's vote is weighted by its honour title, from one vote for a
      Peasant or Knight up to nine for a Crown Prince, so the more allies—and the
      stronger their standing—the greater your chance of securing the throne.
    </p>
  </div>
);

const ResponsibilitiesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      The Monarch&apos;s Responsibilities
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      With great power comes great responsibility. As Monarch, you must:
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>Lead and represent the kingdom in all external affairs.</li>
      <li>Maintain fairness and act with nobility in decision-making.</li>
      <li>Manage the kingdom&apos;s banner and moderate its forums.</li>
      <li>Negotiate diplomatic agreements, including ceasefires.</li>
      <li>
        Lead war efforts, such as declaring war and summoning dragons to weaken
        enemy kingdoms.
      </li>
      <li>
        Set the Kingdom Influence—a bonus based on one of the personalities
        within the kingdom, providing strategic advantages.
      </li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      A Monarch must rule with both strength and wisdom, ensuring that their
      actions reflect the will of the provinces they govern.
    </p>
  </div>
);

const BonusSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      The Monarch&apos;s Bonus
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      To aid in leadership, each Monarch can appoint a Page, an advisor
      responsible for internal communications and forum moderation.
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>
        A Page has moderation privileges in both the kingdom and war forums,
        allowing them to help maintain order.
      </li>
      <li>
        A Monarch may promote a Page to Co-Monarch, granting them full
        leadership powers, ensuring continuity and shared responsibility.
      </li>
      <li>
        Only the Monarch or Co-Monarch can use the Market Takeover item, a key
        tool in kingdom-wide economic control.
      </li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      Effective Monarchs surround themselves with trusted advisors, ensuring
      that leadership remains stable and efficient.
    </p>
  </div>
);

const DefectionSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Defection & Banishment
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      At times, a province may choose—or be forced—to leave the kingdom.
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>
        <strong>Banishment</strong> – If a province is banished, its total
        explored acres are returned to the kingdom&apos;s exploration pool
        (excluding land gained through the Paradise spell).
      </li>
      <li>
        <strong>Defection</strong> – If a province chooses to leave voluntarily,
        its explored land also returns to the exploration pool.
      </li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      Managing the loyalty of your kingdom is crucial. A wise Monarch balances
      authority with diplomacy to maintain a strong, unified realm.
    </p>
  </div>
);

const FinalThoughtsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Final Thoughts</h3>
    <p className="text-[#b8b8b8] mb-2">
      Monarchy is not for the weak or the indecisive. A true leader must inspire
      trust, command respect, and make difficult choices for the good of their
      people. Whether you seek the throne or wish to support your kingdom&apos;s
      ruler, understanding the responsibilities of Monarchy is key to shaping
      the fate of your realm.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Will you rise to rule, or will you serve under another&apos;s banner? The
      choice—and the throne—is yours.
    </p>
  </div>
);

export const Route = createFileRoute('/monarchy/')({
  component: MonarchyPage,
});

function MonarchyPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/monarchy" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/politics@2x.png"
              alt="Monarchy"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Monarchy</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <BecomingMonarchSection />
            <ResponsibilitiesSection />
            <BonusSection />
            <DefectionSection />
            <FinalThoughtsSection />
          </div>
        </section>
      </div>
    </div>
  );
}
